/**
 * ============================================================================
 * CapaWorkbench — Phase 7 frontend (CAPA Module — Corrective & Preventive Action).
 * Owner: Phase 7 — CAPA Module agent.
 * Route: /capa
 * Purpose: top-level QA workbench for CAPA records. Four hero KPI tiles
 *          headline the page (Total Open / Critical Open / Overdue / Closed
 *          Effective This Month), a four-axis filter bar below (status,
 *          criticality, sourceKind, owner), then the CAPA table. "New CAPA"
 *          opens a dialog that creates IDENTIFIED state.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading: skeleton rows (not a spinner-only).
 *   - Error: refined envelope with Retry button.
 *
 * URS rows: CAPA-001..020.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './CapaWorkbench.module.css';
import { NewCapaDialog } from './NewCapaDialog';
import { CapaWorkbenchFilters } from './CapaWorkbenchFilters';
import { CapaRow } from './CapaRow';
import { CapaWorkbenchStats } from './CapaWorkbenchStats';
import {
  useCapasQuery,
  type CapaQueryParams,
  type CapaSummary,
} from './useCapasQuery';

export interface CapaWorkbenchProps {
  /** Optional callback so the host shell can route to the detail page. */
  onOpenCapa?: (id: string) => void;
}

export function CapaWorkbench({ onOpenCapa }: CapaWorkbenchProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CapaQueryParams>({});
  const [showAdd, setShowAdd] = useState(false);

  const capasQ = useCapasQuery(filters);
  const nf = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  );

  const items: CapaSummary[] = capasQ.data?.items ?? [];
  const stats = capasQ.data?.stats;
  const isEmpty = !capasQ.isLoading && !capasQ.isError && items.length === 0;

  function handleOpen(c: CapaSummary) {
    if (onOpenCapa) {
      onOpenCapa(c.capaId);
      return;
    }
    navigate({ to: '/capa/$id', params: { id: c.capaId } });
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('capa.workbench.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('capa.workbench.title', { defaultValue: 'CAPA workbench' })}
        subtitle={t('capa.workbench.subtitle', {
          defaultValue:
            'Quality Assurance view of corrective and preventive actions, their FSM state, criticality, and pending effectiveness verification.',
        })}
        actions={
          <Button
            variant="primary"
            leadingIcon="+"
            onClick={() => setShowAdd(true)}
            aria-label={t('capa.workbench.actions.addAria', {
              defaultValue: 'Open a new CAPA',
            })}
          >
            {t('capa.workbench.actions.add', { defaultValue: 'New CAPA' })}
          </Button>
        }
      />

      <CapaWorkbenchStats stats={stats} isLoading={capasQ.isLoading} />

      <Card>
        <CardHeader>
          <div className={styles.cardHeaderRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('capa.workbench.list.title', { defaultValue: 'CAPAs' })}{' '}
              <span className={styles.muted}>
                ({nf.format(items.length)})
              </span>
            </h2>
            <CapaWorkbenchFilters value={filters} onChange={setFilters} />
          </div>
        </CardHeader>
        <CardBody>
          {capasQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {capasQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('capa.workbench.error', {
                  defaultValue:
                    'Could not load CAPAs. The /api/v1/capa endpoint did not respond.',
                })}
              </span>
              <Button variant="secondary" onClick={() => capasQ.refetch()}>
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {t('capa.workbench.empty.title', {
                  defaultValue: 'No CAPAs match these filters',
                })}
              </h3>
              <p>
                {t('capa.workbench.empty.body', {
                  defaultValue:
                    'CAPAs open here automatically when a deviation closes, an OOS Phase-2 concludes, an audit finding is marked CAPA-required, or a risk-register entry crosses its RPN threshold. You may also open one manually.',
                })}
              </p>
              <Button
                variant="primary"
                leadingIcon="+"
                onClick={() => setShowAdd(true)}
              >
                {t('capa.workbench.actions.add', {
                  defaultValue: 'New CAPA',
                })}
              </Button>
            </div>
          )}

          {!capasQ.isLoading && !capasQ.isError && items.length > 0 && (
            <table className={styles.capaTable}>
              <caption className={styles.srOnly}>
                {t('capa.workbench.list.caption', {
                  defaultValue:
                    'CAPAs with their status, criticality, source, owner, due date, and effectiveness verification posture.',
                })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    {t('capa.workbench.col.number', {
                      defaultValue: 'CAPA #',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.title', {
                      defaultValue: 'Title',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.status', {
                      defaultValue: 'Status',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.criticality', {
                      defaultValue: 'Criticality',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.source', {
                      defaultValue: 'Source',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.due', {
                      defaultValue: 'Due',
                    })}
                  </th>
                  <th scope="col">
                    {t('capa.workbench.col.eff', {
                      defaultValue: 'Eff. verified',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <CapaRow key={c.capaId} capa={c} onOpen={handleOpen} />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <NewCapaDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            capasQ.refetch();
          }}
        />
      )}
    </div>
  );
}
